import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Bar } from "react-chartjs-2";

import { downloadAttritionRecordsCsv } from "./attritionCsv";
import { ChartPanel, DashboardsPageFrame, FilterToggle, KpiCard, KpiGrid } from "./dashboardKit";
import { attritionSummaryQueryOptions, fetchAttritionRecords } from "./dashboardsReactQuery";
import type {
  AttritionOutcome,
  AttritionRecord,
  AttritionRationaleBreakdown,
  AttritionSummaryResponse,
  AttritionTimeframe,
} from "./attritionTypes";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const DASHBOARD_TIMEZONE = "America/New_York";
const ALL_TIME_START_DATE = "2025-07-01";
const DEFAULT_RANGE = "month_to_date";

const COLORS = {
  orange: "#F7941D",
  red: "#C84C35",
  faceOrange: "#FCE5C5",
  faceRed: "#F8E1DC",
};

type RangeKey = "month_to_date" | "past_90_days" | "past_6_months" | "past_12_months" | "all_time";

const RANGE_OPTIONS: Array<{ key: RangeKey; label: string }> = [
  { key: "month_to_date", label: "Month to Date" },
  { key: "past_90_days", label: "Past 90 days" },
  { key: "past_6_months", label: "Past 6 months" },
  { key: "past_12_months", label: "Past 12 months" },
  { key: "all_time", label: "All Time" },
];

function parseIsoDateParts(value: string): { day: number; month: number; year: number } | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

function isoDateToUtcNoon(value: string): Date | null {
  const parts = parseIsoDateParts(value);
  return parts ? new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12)) : null;
}

function shiftIsoDateDays(value: string, deltaDays: number): string {
  const parsed = isoDateToUtcNoon(value);
  if (!parsed) return value;
  parsed.setUTCDate(parsed.getUTCDate() + deltaDays);
  return parsed.toISOString().slice(0, 10);
}

function shiftIsoDateMonths(value: string, deltaMonths: number): string {
  const parsed = isoDateToUtcNoon(value);
  if (!parsed) return value;
  parsed.setUTCMonth(parsed.getUTCMonth() + deltaMonths);
  return parsed.toISOString().slice(0, 10);
}

export function todayIsoInTimezone(timezone: string, referenceDate = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .formatToParts(referenceDate)
    .reduce<Record<string, string>>((result, part) => {
      if (part.type !== "literal") result[part.type] = part.value;
      return result;
    }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function getRangeBoundsInTimezone(
  rangeKey: RangeKey,
  anchorIsoDate: string,
): { dateEnd: string; dateStart: string } {
  if (!parseIsoDateParts(anchorIsoDate)) {
    return { dateStart: anchorIsoDate, dateEnd: anchorIsoDate };
  }
  if (rangeKey === "all_time") {
    return { dateStart: ALL_TIME_START_DATE, dateEnd: anchorIsoDate };
  }
  if (rangeKey === "past_90_days") {
    return {
      dateStart: shiftIsoDateDays(anchorIsoDate, -89),
      dateEnd: anchorIsoDate,
    };
  }
  if (rangeKey === "past_6_months") {
    return {
      dateStart: shiftIsoDateDays(shiftIsoDateMonths(anchorIsoDate, -6), 1),
      dateEnd: anchorIsoDate,
    };
  }
  if (rangeKey === "past_12_months") {
    return {
      dateStart: shiftIsoDateDays(shiftIsoDateMonths(anchorIsoDate, -12), 1),
      dateEnd: anchorIsoDate,
    };
  }
  const parts = parseIsoDateParts(anchorIsoDate);
  if (!parts) {
    return { dateStart: anchorIsoDate, dateEnd: anchorIsoDate };
  }
  return {
    dateStart: `${parts.year}-${String(parts.month).padStart(2, "0")}-01`,
    dateEnd: anchorIsoDate,
  };
}

function formatDate(value: string | undefined, includeTime = false): string {
  if (!value) return "—";
  const parsed = isoDateToUtcNoon(value) || new Date(value);
  if (Number.isNaN(parsed.valueOf())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DASHBOARD_TIMEZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(includeTime ? { hour: "numeric", minute: "2-digit" } : {}),
  }).format(parsed);
}

function asCount(value: number): string {
  return Math.round(Number(value) || 0).toLocaleString();
}

function asPct(value: number): string {
  return `${((Number(value) || 0) * 100).toFixed(1)}%`;
}

function RationaleChart(props: {
  color: string;
  items: AttritionRationaleBreakdown[];
  onReasonSelect: (reason: string) => void;
  selectedReason: string;
  subtitle: string;
  title: string;
  total: number;
  pendingUnalignmentCount: number;
}) {
  const labels = props.items.map((item) => item.rationale);
  const data = {
    labels,
    datasets: [
      {
        data: props.items.map((item) => item.count),
        backgroundColor: props.items.map((item) =>
          props.selectedReason && props.selectedReason !== item.rationale
            ? `${props.color}66`
            : props.color,
        ),
        borderRadius: 4,
        barThickness: 14,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    onClick: (_event, elements) => {
      const index = elements[0]?.index;
      if (index === undefined) return;
      const reason = labels[index];
      if (reason) props.onReasonSelect(reason);
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const item = props.items[context.dataIndex];
            if (!item) return "";
            const pct = props.total > 0 ? item.count / props.total : 0;
            const sources = item.sourceRationales.join(" | ");
            return [
              `${asCount(item.count)} carespaces (${asPct(pct)})`,
              `Original rationale${item.sourceRationales.length === 1 ? "" : "s"}: ${sources}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { color: "#ECE6DD" },
      },
      y: {
        ticks: {
          autoSkip: false,
          font: { size: 11 },
        },
        grid: { display: false },
      },
    },
  };

  const height = Math.max(160, props.items.length * 36 + 40);

  return (
    <ChartPanel
      title={props.title}
      action={
        <div className="flex flex-wrap items-center justify-end gap-2 text-xs text-muted-foreground">
          {props.pendingUnalignmentCount ? (
            <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 font-semibold text-red-700">
              {asCount(props.pendingUnalignmentCount)} pending unalignment PAF
              {props.pendingUnalignmentCount === 1 ? "" : "s"}
            </span>
          ) : null}
          <span>{props.subtitle}</span>
        </div>
      }
    >
      {props.items.length ? (
        <div className="relative" style={{ height }}>
          <Bar data={data} options={options} />
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No carespaces in this cohort for the selected period.
        </p>
      )}
    </ChartPanel>
  );
}

function AttritionRecordCard(props: { color: string; face: string; record: AttritionRecord }) {
  return (
    <article className="rounded-lg border border-border/70 bg-background/90 p-3.5">
      <div className="flex items-start justify-between gap-3">
        <a
          href={props.record.carespaceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-semibold text-blue-700 hover:underline"
        >
          {props.record.carespaceName || "Carespace"}
        </a>
        <time
          dateTime={props.record.effectiveDate}
          className="shrink-0 text-xs text-muted-foreground"
        >
          {formatDate(props.record.effectiveDate)}
        </time>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {[props.record.clinic, props.record.recordedBy && `Recorded by ${props.record.recordedBy}`]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <span
        className="mt-2.5 inline-block rounded-full border px-2 py-0.5 text-[0.7rem] font-bold"
        style={{
          color: props.color,
          background: props.face,
          borderColor: `${props.color}33`,
        }}
      >
        {props.record.reasonCategory}
      </span>
      {props.record.hasPendingUnalignmentPaf ? (
        <span className="mt-2.5 ml-1 inline-block rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[0.7rem] font-bold text-red-700">
          Pending unalignment PAF
        </span>
      ) : null}
      <div className="mt-2.5 border-l-[3px] border-border bg-muted/40 px-3 py-2">
        <div className="text-[0.65rem] font-bold tracking-wide text-muted-foreground uppercase">
          Narrative
        </div>
        {props.record.narrative ? (
          <p className="mt-1 text-xs leading-relaxed whitespace-pre-wrap text-foreground">
            {props.record.narrative}
          </p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground italic">No narrative recorded.</p>
        )}
      </div>
    </article>
  );
}

function DrilldownPanel(props: {
  color: string;
  error: string;
  face: string;
  loading: boolean;
  loadingMore: boolean;
  exporting: boolean;
  exportError: string;
  onClearReason: () => void;
  onDownload: () => void;
  onLoadMore: () => void;
  records: AttritionRecord[];
  selectedReason: string;
  totalCount: number;
}) {
  return (
    <aside className="min-w-0 rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
      <header className="flex items-start justify-between gap-3 border-b border-border/60 pb-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Carespace drill down</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {props.loading
              ? "Loading records…"
              : `${asCount(props.totalCount)} ${props.totalCount === 1 ? "record" : "records"}`}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {props.selectedReason ? (
            <button
              type="button"
              onClick={props.onClearReason}
              className="rounded-md border border-border px-2 py-1 text-xs font-semibold text-muted-foreground hover:bg-accent"
            >
              Clear filter
            </button>
          ) : null}
          <button
            type="button"
            disabled={props.loading || props.exporting || props.totalCount === 0}
            onClick={props.onDownload}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {props.exporting ? "Preparing CSV…" : "Download CSV"}
          </button>
        </div>
      </header>

      {props.exportError ? (
        <p role="alert" className="mt-3 text-xs text-destructive">
          {props.exportError}
        </p>
      ) : null}

      {props.selectedReason ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Reason: <strong className="text-foreground">{props.selectedReason}</strong>
        </p>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">
          Select a reason bar to filter these records.
        </p>
      )}

      {props.error ? (
        <div className="grid min-h-44 place-items-center text-center text-sm text-destructive">
          {props.error}
        </div>
      ) : null}
      {!props.error && props.loading ? (
        <div className="grid min-h-44 place-items-center text-sm text-muted-foreground">
          Loading records…
        </div>
      ) : null}
      {!props.error && !props.loading && !props.records.length ? (
        <div className="grid min-h-44 place-items-center text-sm text-muted-foreground">
          No matching carespaces for this period.
        </div>
      ) : null}

      {props.records.length ? (
        <div className="mt-3 grid max-h-[570px] gap-2.5 overflow-y-auto pr-1">
          {props.records.map((record) => (
            <AttritionRecordCard
              key={record.carespaceId}
              record={record}
              color={props.color}
              face={props.face}
            />
          ))}
          {props.records.length < props.totalCount ? (
            <button
              type="button"
              disabled={props.loadingMore}
              onClick={props.onLoadMore}
              className="justify-self-center rounded-md border border-border bg-background px-3 py-2 text-xs font-bold text-muted-foreground disabled:opacity-60"
            >
              {props.loadingMore ? "Loading…" : "Load more"}
            </button>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}

function OutcomeSection(props: {
  badge: string;
  color: string;
  count: number;
  description: string;
  face: string;
  id: string;
  outcome: AttritionOutcome;
  rationaleSubtitle: string;
  rationaleTitle: string;
  rationales: AttritionRationaleBreakdown[];
  pendingUnalignmentCount: number;
  timeframe: AttritionTimeframe;
  title: string;
}) {
  const headingId = `${props.id}-heading`;
  const [selectedReason, setSelectedReason] = useState("");
  const [records, setRecords] = useState<AttritionRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recordsError, setRecordsError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const activeRequestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++activeRequestIdRef.current;
    let cancelled = false;
    setRecordsLoading(true);
    setLoadingMore(false);
    setRecordsError("");
    setExportError("");
    setRecords([]);
    setTotalCount(0);

    fetchAttritionRecords({
      ...props.timeframe,
      outcome: props.outcome,
      reason: selectedReason,
    })
      .then((payload) => {
        if (cancelled || requestId !== activeRequestIdRef.current) return;
        setRecords(payload.rows);
        setTotalCount(payload.totalCount);
      })
      .catch(() => {
        if (!cancelled && requestId === activeRequestIdRef.current) {
          setRecordsError("Attrition records are unavailable. Please retry.");
        }
      })
      .finally(() => {
        if (!cancelled && requestId === activeRequestIdRef.current) {
          setRecordsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (requestId === activeRequestIdRef.current) {
        activeRequestIdRef.current += 1;
      }
    };
  }, [props.outcome, props.timeframe, selectedReason]);

  const handleReasonSelect = (reason: string) => {
    activeRequestIdRef.current += 1;
    setSelectedReason((current) => (current === reason ? "" : reason));
  };

  const handleClearReason = () => {
    activeRequestIdRef.current += 1;
    setSelectedReason("");
  };

  const handleDownload = async () => {
    if (exporting || recordsLoading || totalCount === 0) return;
    setExporting(true);
    setExportError("");
    try {
      await downloadAttritionRecordsCsv({
        ...props.timeframe,
        outcome: props.outcome,
        reason: selectedReason,
      });
    } catch {
      setExportError("Attrition records could not be downloaded. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleLoadMore = async () => {
    const requestId = activeRequestIdRef.current;
    setLoadingMore(true);
    try {
      const payload = await fetchAttritionRecords({
        ...props.timeframe,
        outcome: props.outcome,
        reason: selectedReason,
        offset: records.length,
      });
      if (requestId !== activeRequestIdRef.current) return;
      setRecords((current) => [...current, ...payload.rows]);
      setTotalCount(payload.totalCount);
    } catch {
      if (requestId === activeRequestIdRef.current) {
        setRecordsError("More attrition records could not be loaded.");
      }
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoadingMore(false);
      }
    }
  };

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-xl border border-border/70 bg-muted/20 shadow-xs"
    >
      <div className="h-1.5" style={{ background: props.color }} />
      <div className="p-4 sm:p-5">
        <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-3xl">
            <h2 id={headingId} className="text-lg font-semibold text-foreground">
              {props.title}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {props.description}
            </p>
          </div>
          <span
            className="rounded-full border px-2.5 py-1 text-[0.72rem] font-bold whitespace-nowrap"
            style={{
              color: props.color,
              background: props.face,
              borderColor: `${props.color}33`,
            }}
          >
            {props.badge}
          </span>
        </header>

        <div className="grid items-stretch gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <RationaleChart
            title={props.rationaleTitle}
            subtitle={props.rationaleSubtitle}
            items={props.rationales}
            total={props.count}
            pendingUnalignmentCount={props.pendingUnalignmentCount}
            color={props.color}
            selectedReason={selectedReason}
            onReasonSelect={handleReasonSelect}
          />
          <DrilldownPanel
            selectedReason={selectedReason}
            onClearReason={handleClearReason}
            records={records}
            totalCount={totalCount}
            loading={recordsLoading}
            loadingMore={loadingMore}
            exporting={exporting}
            exportError={exportError}
            error={recordsError}
            onLoadMore={handleLoadMore}
            onDownload={() => void handleDownload()}
            color={props.color}
            face={props.face}
          />
        </div>
      </div>
    </section>
  );
}

function MetricAccentCard(props: {
  color: string;
  count: number;
  face: string;
  label: string;
  rate: number;
  sublabel: string;
}) {
  return (
    <article className="overflow-hidden rounded-lg border border-border/70 bg-background shadow-xs">
      <div className="h-1.5" style={{ background: props.color }} />
      <div className="px-4 py-3.5" style={{ background: props.face }}>
        <p className="text-[0.7rem] font-bold tracking-wide text-muted-foreground uppercase">
          {props.label}
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight text-foreground">
          {asCount(props.count)}
        </p>
        <p className="mt-1.5 text-sm font-bold" style={{ color: props.color }}>
          {asPct(props.rate)}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{props.sublabel}</p>
      </div>
    </article>
  );
}

function AttritionContent(props: {
  selectedRange: RangeKey;
  setSelectedRange: (range: RangeKey) => void;
  summary: AttritionSummaryResponse;
  timeframe: AttritionTimeframe;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {RANGE_OPTIONS.map((option) => (
          <FilterToggle
            key={option.key}
            active={option.key === props.selectedRange}
            onClick={() => props.setSelectedRange(option.key)}
          >
            {option.label}
          </FilterToggle>
        ))}
        <button
          type="button"
          onClick={() => props.setSelectedRange(DEFAULT_RANGE)}
          className="rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold text-foreground hover:bg-accent"
        >
          Reset
        </button>
      </div>

      <div className="rounded-lg border border-border/70 bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
        Window: {formatDate(props.timeframe.dateStart)} to {formatDate(props.timeframe.dateEnd)}.
        Percentages use the period&apos;s post-alignment cohort: aligned + archived + unaligned. A
        carespace with a pending unalignment PAF and an archive is counted as unaligned and marked
        as pending below.
      </div>

      <section aria-label="Attrition overview" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricAccentCard
          label="Unaligned"
          count={props.summary.unalignedCount}
          rate={props.summary.unalignedPct}
          sublabel="True losses · share of post-alignment cohort"
          color={COLORS.red}
          face={COLORS.faceRed}
        />
        <MetricAccentCard
          label="Unengaged"
          count={props.summary.archivedCount}
          rate={props.summary.archivedPct}
          sublabel="Potentially recoverable · share of post-alignment cohort"
          color={COLORS.orange}
          face={COLORS.faceOrange}
        />
      </section>

      <KpiGrid>
        <KpiCard
          label="Post-alignment cohort"
          value={asCount(props.summary.postAlignmentCohortCount)}
        />
        <KpiCard label="Aligned" value={asCount(props.summary.alignedCount)} />
        <KpiCard label="Unaligned" value={asCount(props.summary.unalignedCount)} />
        <KpiCard label="Unengaged" value={asCount(props.summary.archivedCount)} />
      </KpiGrid>

      <div className="grid gap-4">
        <OutcomeSection
          key={`unaligned-${props.timeframe.dateStart}-${props.timeframe.dateEnd}`}
          id="unaligned"
          outcome="unaligned"
          timeframe={props.timeframe}
          title="Unaligned"
          description="Carespaces removed from GUIDE that are no longer billable without re-enrollment."
          badge="True loss"
          count={props.summary.unalignedCount}
          rationales={props.summary.unalignedRationales}
          pendingUnalignmentCount={props.summary.pendingUnalignmentCount}
          rationaleTitle="Unaligned reasons"
          rationaleSubtitle="Share of unaligned carespaces in the selected period"
          color={COLORS.red}
          face={COLORS.faceRed}
        />
        <OutcomeSection
          key={`unengaged-${props.timeframe.dateStart}-${props.timeframe.dateEnd}`}
          id="unengaged"
          outcome="archived"
          timeframe={props.timeframe}
          title="Unengaged"
          description="Archived carespaces that may be recovered and become billable again after re-engagement."
          badge="Potentially recoverable"
          count={props.summary.archivedCount}
          rationales={props.summary.archivedRationales}
          pendingUnalignmentCount={0}
          rationaleTitle="Unengaged reasons"
          rationaleSubtitle="Share of archived carespaces in the selected period"
          color={COLORS.orange}
          face={COLORS.faceOrange}
        />
      </div>
    </>
  );
}

function SourceBadge(props: { children: ReactNode; live: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={`size-1.5 rounded-full ${props.live ? "bg-emerald-600 shadow-[0_0_0_3px_rgb(47_133_90_/_12%)]" : "bg-stone-400"}`}
      />
      {props.children}
    </span>
  );
}

export function AttritionDashboard() {
  const [selectedRange, setSelectedRange] = useState<RangeKey>(DEFAULT_RANGE);

  const timeframe = useMemo<AttritionTimeframe>(() => {
    const anchorDate = todayIsoInTimezone(DASHBOARD_TIMEZONE);
    return {
      ...getRangeBoundsInTimezone(selectedRange, anchorDate),
      timezone: DASHBOARD_TIMEZONE,
    };
  }, [selectedRange]);

  const query = useQuery(attritionSummaryQueryOptions(timeframe));

  return (
    <DashboardsPageFrame className="flex flex-col gap-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Attrition Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            GUIDE outcomes during the selected period, measured against the post-alignment cohort.
          </p>
        </div>
        <div className="flex items-center gap-2.5 text-xs font-semibold text-muted-foreground">
          <SourceBadge live={query.data?.source === "live"}>
            Data: {query.data?.source === "live" ? "LIVE" : query.data ? "MOCK" : "—"}
          </SourceBadge>
          <span aria-hidden="true" className="h-4 w-px bg-border" />
          <span>Updated: {formatDate(query.data?.generatedAt, true)}</span>
        </div>
      </header>

      {query.isPending ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading Attrition…
        </div>
      ) : null}

      {query.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : "Attrition data is unavailable. Please try again."}
        </div>
      ) : null}

      {query.data ? (
        <AttritionContent
          summary={query.data}
          timeframe={timeframe}
          selectedRange={selectedRange}
          setSelectedRange={setSelectedRange}
        />
      ) : null}
    </DashboardsPageFrame>
  );
}
